import { FieldAttributes, useField, useFormikContext } from 'formik';
import React, { useCallback, useMemo } from 'react';
import { Form, OverlayTrigger } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import BootstrapSwitchButton from './../bootstrap-switch-button/bootstrap-switch-button-react';
import { FieldLabel } from './../index';
import FieldTooltip from './field-tooltip';

type Props = {
  readonly [key: string]: unknown;
  readonly as?: React.ElementType<unknown>;
  readonly label: string;
  readonly onLabel?: string;
  readonly offLabel?: string;
  readonly width?: number;
  readonly height?: number;
  readonly onStyle?: string;
  readonly offStyle?: string;
  readonly disabled?: boolean;
  // eslint-disable-off-next-line-off functional/no-return-void
  readonly onChange?: (value: boolean, fieldName: string) => void;
  readonly helpText?: string | React.ReactNode;
} & FieldAttributes<unknown>;

const FieldCheckbox = ({
  as = 'div',
  name,
  label,
  onLabel,
  offLabel,
  width = 80,
  height = 22,
  disabled = false,
  onStyle = undefined,
  offStyle = undefined,
  onChange = undefined,
  helpText = undefined,
}: Props) => {
  const { t, i18n } = useTranslation();
  onLabel = onLabel ? onLabel : i18n.language === 'gr' ? t('Ναι') : t('Yes');
  offLabel = offLabel ? offLabel : i18n.language === 'gr' ? t('Όχι') : t('No');
  //
  //-------------------- Body -----------------------------------------------
  //
  //console.log('FieldCheckBox -> React Version', React.version)
  const [{ value }, { touched, error, initialValue }] = useField<boolean>(name);
  const { setFieldValue, setFieldTouched, submitCount } = useFormikContext<any>();

  const setFieldValueAndTouched = useCallback(
    async (value, touched: boolean) => {
      await setFieldValue(name, value, true); //field,value,shouldValidate
      await setFieldTouched(name, touched); //field,touched,shouldValidate
    },
    [name, setFieldTouched, setFieldValue]
  );
  //
  //-------------------- Handlers -------------------------------------------
  //
  const handleChange = useCallback(
    (checked) => {
      setFieldValueAndTouched(checked, true);
      if (onChange) onChange(checked, name);
    },
    [onChange, setFieldValueAndTouched]
  );

  const changed = value !== initialValue;

  // useWhyDidYouUpdate(`[FieldCheckbox:${name}]`, {
  //   label,
  //   disabled,
  //   name,
  //   handleChange,
  //   setFieldValueAndTouched,
  //   value,
  //   initialValue,
  // });
  //
  //-------------------- Render -------------------------------------------
  //
  return useMemo(
    () => (
      <Form.Group
        as={as as any}
        controlId={'formGroup_' + name}
        className="mb-2 field-checkbox"
      >
        <FieldLabel text={label} changed={changed} helpText={helpText} />
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <OverlayTrigger
            delay={{ show: 1000, hide: 200 }}
            overlay={
              <FieldTooltip text={name} value={value ? onLabel : offLabel} />
            }
            placement="top"
          >
            <div>
              <BootstrapSwitchButton
                checked={value || false}
                onlabel={onLabel}
                offlabel={offLabel}
                width={width}
                height={height}
                onChange={handleChange}
                onstyle={disabled ? 'dark' : (onStyle ?? 'primary')}
                offstyle={disabled ? 'light' : (offStyle ?? 'warning')}
                disabled={disabled}
                size="sm"
              />
            </div>
          </OverlayTrigger>
        </div>
        {/* <FieldFeedback type="invalid" name={name} /> */}
        {(touched || submitCount > 0) && (
          <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
        )}
      </Form.Group>
    ),
    [
      as,
      name,
      label,
      changed,
      value,
      onLabel,
      offLabel,
      width,
      height,
      handleChange,
      touched,
      error,
      submitCount,
      onStyle,
      disabled,
      offStyle,
    ]
  );
};

export default FieldCheckbox;
